/**
 * Code inside a chat message: the lazy CodeBlock wrapper plus the two parsers
 * that decide what counts as code.
 *
 * Split out of BirdMessage so the pure parsers are reachable from a test
 * without importing a 700-line component module (and its Firebase-bearing
 * import graph).
 *
 * Two parsers because the two turns arrive differently: Bird's markdown is
 * already parsed into a hast tree (readFencedCode), while the user's raw text
 * is deliberately NOT markdown-rendered (splitFencedSegments).
 */

import { Skeleton } from '@chakra-ui/react';
import { Suspense } from 'react';
import type { Element as HastElement } from 'hast';

import { lazyWithRetry } from '../../../utils/lazyWithRetry';
import { ErrorBoundary, ComponentErrorFallback } from '../../ErrorBoundary';
import type { CodeBlock as CodeBlockType } from '../../ui/CodeBlock';

// Lazy for the same reason as ChartBlock: the highlight.js grammars are the
// bulk of the weight and most turns carry no code at all.
// Exported so a test can warm the chunk through the SAME factory this lazy
// binding uses. Hand-copying the specifier into a test lets a repoint here
// silently desync it (see BirdMessageCode.test.tsx).
export const loadChatCodeBlock = () =>
  import('../../ui/CodeBlock').then((m) => ({ default: m.CodeBlock as typeof CodeBlockType }));

const CodeBlock = lazyWithRetry(loadChatCodeBlock);

/**
 * The chat's one entry point to the shared code surface. Both the assistant's
 * fences and the user's own pasted code go through here so the boundary +
 * loading treatment can't drift between the two.
 */
export function ChatCodeBlock({
  code,
  language,
  infoString,
}: {
  readonly code: string;
  readonly language: string | null;
  readonly infoString?: string | null;
}) {
  return (
    <ErrorBoundary level="component" fallback={<ComponentErrorFallback />}>
      <Suspense fallback={<Skeleton h="64px" borderRadius="10px" mb={3} />}>
        <CodeBlock code={code} language={language} infoString={infoString} surface="chat" />
      </Suspense>
    </ErrorBoundary>
  );
}

export interface FencedCode {
  /** The fence body, exactly as authored, minus rehype's trailing newline. */
  readonly code: string;
  /** First info-string token — what rehype turns into `language-…`. */
  readonly hint: string | null;
  /** The whole info string as typed, chunk options included. */
  readonly infoString: string | null;
}

/**
 * Read a fenced block straight off the hast node react-markdown hands `pre`.
 *
 * Reading the TREE rather than the rendered children is what lets the fenced
 * and inline cases be distinguished structurally: rehype emits `<pre><code>`
 * for a fence and a bare `<code>` for inline, so `pre` IS the block signal.
 * Inferring it instead ("has a className, or the text has a newline") got an
 * untagged single-line fence wrong, and `String(children)` on an empty fence
 * coerced `undefined` into the literal text "undefined".
 *
 * The info string is reassembled from two halves because rehype splits it:
 * `{r setup, include=FALSE}` becomes className `language-{r` plus
 * `data.meta` `setup, include=FALSE}`. Rejoining them is the only way the
 * chunk options survive to the header.
 */
export function readFencedCode(node: HastElement | undefined): FencedCode | null {
  const codeNode = node?.children.find(
    (child): child is HastElement => child.type === 'element' && child.tagName === 'code',
  );
  if (!codeNode) return null;

  const classNames = codeNode.properties?.className;
  const hint =
    (Array.isArray(classNames) ? classNames : [])
      .map((value) => String(value))
      .find((value) => value.startsWith('language-'))
      ?.slice('language-'.length) ?? null;

  const meta = typeof codeNode.data?.meta === 'string' ? codeNode.data.meta : '';
  const code = codeNode.children
    .map((child) => (child.type === 'text' ? child.value : ''))
    .join('')
    .replace(/\n$/, '');

  return { code, hint, infoString: [hint, meta].filter(Boolean).join(' ') || null };
}

export interface FencedSegment {
  readonly kind: 'text' | 'code';
  readonly body: string;
  /** The info string as typed — chunk header and all — or null if untagged. */
  readonly language: string | null;
}

/**
 * Split user content on fence boundaries. Only fenced segments get code
 * treatment — deliberately NOT full markdown, so prose in user turns keeps
 * rendering exactly as typed.
 *
 * The info-string capture is `[^\n]*`, not `[\w-]*`: an R Markdown chunk
 * header (```{r setup, include=FALSE}) is the likeliest thing an RStudio user
 * pastes, and a word-character class drops it on the floor.
 */
export function splitFencedSegments(content: string): readonly FencedSegment[] {
  const segments: FencedSegment[] = [];
  const re = /```([^\n]*)\n([\s\S]*?)```/g;
  let last = 0;
  for (let m = re.exec(content); m !== null; m = re.exec(content)) {
    if (m.index > last) {
      segments.push({ kind: 'text', body: content.slice(last, m.index), language: null });
    }
    segments.push({
      kind: 'code',
      body: m[2].replace(/\n$/, ''),
      language: m[1].trim() || null,
    });
    last = m.index + m[0].length;
  }
  if (last < content.length) {
    segments.push({ kind: 'text', body: content.slice(last), language: null });
  }
  return segments;
}
